import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { BookOpen } from "lucide-react";
import { z } from "zod";

import { EmptyState } from "@/components/empty-state";
import { StoryCard, StoryCardSkeleton } from "@/components/story-card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { MembersOnlyGate } from "@/components/members-only-gate";
import { useAuth } from "@/lib/auth-context";
import { useI18n } from "@/lib/i18n";
import { fetchCategories, fetchContentTypes, fetchPublishedStories } from "@/lib/queries";
import { cn } from "@/lib/utils";
import { TagChip } from "@/components/tag-chip";
import { fetchTrendingTags, normaliseTagQuery, searchTags } from "@/lib/tags";
import { TrendingTagChip } from "@/components/trending-tag-chip";
import { useTagPreferences } from "@/lib/use-tag-preferences";

const searchSchema = z.object({
  q: z.string().optional(),
  category: z.string().optional(),
  type: z.string().optional(),
  lang: z.string().optional(),
  sort: z.enum(["published_at", "like_count", "view_count"]).optional(),
});

export const Route = createFileRoute("/discover")({
  validateSearch: searchSchema,
  head: () => ({
    meta: [
      { title: "Discover original writing — Quibblies" },
      {
        name: "description",
        content:
          "Browse fiction, personal experiences, reflections, poetry and series from the Quibblies community. Filter by content type, category and what's trending.",
      },
      { property: "og:title", content: "Discover original writing — Quibblies" },
      {
        property: "og:description",
        content: "Browse published writing from the Quibblies community.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: DiscoverPage,
});

const SORTS = [
  { value: "published_at", label: "Newest" },
  { value: "like_count", label: "Most liked" },
  { value: "view_count", label: "Most read" },
] as const;

function DiscoverPage() {
  const { user } = useAuth();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: "/discover" });
  const sort = search.sort ?? "published_at";
  const { t, preferences } = useI18n();

  // An explicit language filter always wins; otherwise the member's own feed
  // preference applies, and everyone else sees the whole global community.
  const languageFilter = search.lang
    ? [search.lang]
    : preferences.only_my_languages
      ? preferences.content_languages
      : undefined;

  const categories = useQuery({ queryKey: ["categories"], queryFn: fetchCategories });
  const contentTypes = useQuery({ queryKey: ["content-types"], queryFn: fetchContentTypes });
  const languages = useQuery({
    queryKey: ["languages"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("languages")
        .select("code, name, native_name")
        .eq("archived", false)
        .order("position");
      if (error) throw error;
      return data ?? [];
    },
  });
  const stories = useQuery({
    queryKey: [
      "stories",
      "discover",
      search.q ?? "",
      search.category ?? "",
      search.type ?? "",
      (languageFilter ?? []).join(","),
      sort,
    ],
    queryFn: () =>
      fetchPublishedStories({
        limit: 36,
        orderBy: sort,
        ...(search.q ? { search: search.q } : {}),
        ...(search.category ? { categorySlug: search.category } : {}),
        ...(search.type ? { contentTypeSlug: search.type } : {}),
        ...(languageFilter ? { languages: languageFilter } : {}),
      }),
  });


  // Global search understands hashtags, with or without the leading #.
  const tagQuery = normaliseTagQuery(search.q ?? "");
  const matchingTags = useQuery({
    queryKey: ["tag-search", "discover", tagQuery],
    queryFn: () => searchTags(tagQuery, 6),
    enabled: tagQuery.length >= 2,
  });
  // People search: members can look each other up by pen/display name or handle.
  const peopleQuery = (search.q ?? "").trim();
  const people = useQuery({
    queryKey: ["people-search", peopleQuery],
    enabled: peopleQuery.length >= 2,
    queryFn: async () => {
      const term = `%${peopleQuery.replace(/[%_]/g, "")}%`;
      const { data, error } = await supabase
        .from("profiles")
        .select("id, username, display_name, bio, avatar_url, member_type")
        .or(`username.ilike.${term},display_name.ilike.${term}`)
        .limit(12);
      if (error) throw error;
      return data ?? [];
    },
  });

  const trending = useQuery({ queryKey: ["trending-tags"], queryFn: () => fetchTrendingTags(12) });
  const { applyMutes, rankByFollowed } = useTagPreferences();

  const visibleStories = rankByFollowed(applyMutes(stories.data ?? []));

  function setSearch(next: Partial<z.infer<typeof searchSchema>>) {
    void navigate({ search: (prev) => ({ ...prev, ...next }) });
  }

  if (!user) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 sm:px-6">
        <MembersOnlyGate
          title="Discover is for members"
          description="Sign in to browse the writing our members publish. Quibblies is an 18+ community and publications are visible to signed-in members only."
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
      <header className="mb-8">
        <h1 className="font-display text-4xl text-foreground">Discover</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Fiction, personal experiences, reflections and poetry — every publication here has passed
          moderation review.
        </p>
      </header>

      <form
        className="mb-6 flex flex-wrap gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          const value = new FormData(event.currentTarget).get("q");
          setSearch({ q: typeof value === "string" && value ? value : undefined });
        }}
      >
        <Input
          name="q"
          defaultValue={search.q ?? ""}
          placeholder="Search publications, people or #hashtags"
          className="max-w-sm"
          aria-label="Search publications"
          maxLength={80}
        />
        <Button type="submit" variant="soft">
          Search
        </Button>
      </form>

      {tagQuery.length >= 2 && (matchingTags.data ?? []).length > 0 ? (
        <section className="mb-6 rounded-3xl border border-border/70 bg-surface/40 p-4">
          <h2 className="text-xs uppercase tracking-wide text-muted-foreground">Hashtags</h2>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {matchingTags.data!.map((tag) => (
              <TagChip key={tag.id} tag={tag} size="md" />
            ))}
          </div>
        </section>
      ) : null}

      {peopleQuery.length >= 2 && (people.data ?? []).length > 0 ? (
        <section className="mb-6 rounded-3xl border border-border/70 bg-surface/40 p-4">
          <h2 className="text-xs uppercase tracking-wide text-muted-foreground">People</h2>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {people.data!.map((person) => (
              <Link
                key={person.id}
                to="/u/$username"
                params={{ username: person.username }}
                className="flex items-center gap-3 rounded-2xl border border-border/60 bg-background/40 p-3 transition-smooth hover:border-primary/50"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full border border-border bg-accent text-xs font-semibold text-accent-foreground">
                  {person.avatar_url ? (
                    <img src={person.avatar_url} alt="" className="h-full w-full object-cover" />
                  ) : (
                    person.display_name.slice(0, 1).toUpperCase()
                  )}
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-foreground">
                    {person.display_name}
                  </span>
                  <span className="block truncate text-xs text-muted-foreground">
                    @{person.username} · {person.member_type}
                  </span>
                </span>
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      {(trending.data ?? []).length > 0 ? (
        <section className="mb-8 rounded-3xl border border-border/70 bg-surface/40 p-4">
          <h2 className="text-xs uppercase tracking-wide text-muted-foreground">Trending tags</h2>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {trending.data!.map((tag) => (
              <TrendingTagChip key={tag.id} tag={tag} score={Number(tag.score)} />
            ))}
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">
            Based on recent reads, likes, saves and new publications — not lifetime totals. Hover a
            hashtag to see what drove its score.
          </p>
        </section>
      ) : null}

      <div className="mb-6 flex flex-wrap gap-2">
        {SORTS.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => setSearch({ sort: option.value })}
            className={cn(
              "rounded-full border border-border/70 px-4 py-1.5 text-sm transition-smooth",
              sort === option.value
                ? "border-primary/60 bg-accent text-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setSearch({ type: undefined })}
          className={cn(
            "rounded-full border border-border/70 px-4 py-1.5 text-sm transition-smooth",
            !search.type
              ? "border-primary/60 bg-accent text-foreground"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          All writing
        </button>
        {(contentTypes.data ?? []).map((type) => (
          <button
            key={type.id}
            type="button"
            onClick={() => setSearch({ type: type.slug })}
            className={cn(
              "rounded-full border border-border/70 px-4 py-1.5 text-sm transition-smooth",
              search.type === type.slug
                ? "border-primary/60 bg-accent text-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {type.name}
          </button>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className="text-xs uppercase tracking-wide text-muted-foreground">
          {t("discover.language")}
        </span>
        <button
          type="button"
          onClick={() => setSearch({ lang: undefined })}
          className={cn(
            "rounded-full border border-border/70 px-4 py-1.5 text-sm transition-smooth",
            !search.lang
              ? "border-primary/60 bg-accent text-foreground"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          {t("discover.allLanguages")}
        </button>
        {(languages.data ?? []).map((language) => (
          <button
            key={language.code}
            type="button"
            onClick={() => setSearch({ lang: language.code })}
            className={cn(
              "rounded-full border border-border/70 px-4 py-1.5 text-sm transition-smooth",
              search.lang === language.code
                ? "border-primary/60 bg-accent text-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {language.native_name}
          </button>
        ))}
      </div>


      <div className="mb-8 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setSearch({ category: undefined })}
          className={cn(
            "rounded-full border border-border/70 px-4 py-1.5 text-sm transition-smooth",
            !search.category
              ? "border-primary/60 bg-accent text-foreground"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          All categories
        </button>
        {(categories.data ?? []).map((category) => (
          <button
            key={category.id}
            type="button"
            onClick={() => setSearch({ category: category.slug })}
            className={cn(
              "rounded-full border border-border/70 px-4 py-1.5 text-sm transition-smooth",
              search.category === category.slug
                ? "border-primary/60 bg-accent text-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {category.name}
          </button>
        ))}
      </div>

      {stories.isLoading ? (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <StoryCardSkeleton key={index} />
          ))}
        </div>
      ) : visibleStories.length === 0 ? (
        <EmptyState
          icon={BookOpen}
          title="No publications match your filters"
          description="Try a different content type or category, or clear your search to see everything published."
        />
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {visibleStories.map((story) => (
            <StoryCard key={story.id} story={story} />
          ))}
        </div>
      )}
    </div>
  );
}
